import React, { useState, useEffect } from 'react';
import { Check, GripVertical, ListOrdered, Megaphone, Plus, X } from 'lucide-react';
import { staffClient } from 'clients/staff';
import { categoryApi, Category } from 'apis/category';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import Table, { Column } from 'ui/Table';
import StatCard from 'ui/StatCard';
import Loading from 'ui/Loading';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import StatsGrid from 'ui/StatsGrid';
import TableContainer from 'ui/TableContainer';
import Button from 'ui/Button';
import Input from 'ui/Input';
import Modal from 'ui/Modal';
import { Badge } from 'ui/shadcn';
import { cn } from 'lib/utils';

interface Board {
    id: number;
    name: string;
    description: string;
    board_type?: 'general' | 'document' | 'notice';
    visible: boolean;
    order: number;
    created_at: string;
    updated_at: string;
    categories?: Category[];
}

interface CategoryItem {
    id: string;
    name: string;
    order: number;
}

const textareaClass = cn(
    'flex w-full rounded border border-input bg-background px-3 py-2 text-base text-foreground transition-colors duration-fast',
    'placeholder:text-neutral-400',
    'focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring'
);

const BoardManagement: React.FC = () => {
    const [boards, setBoards] = useState<Board[]>([]);
    const [noticeBoard, setNoticeBoard] = useState<Board | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);  // 전체 카테고리 통계용
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [editingBoard, setEditingBoard] = useState<Board | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        visible: true
    });

    // 카테고리 관리 상태
    const [boardCategories, setBoardCategories] = useState<CategoryItem[]>([]);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [editingCategoryName, setEditingCategoryName] = useState('');

    // 게시판 순서 관리 상태
    const [sortedBoards, setSortedBoards] = useState<Board[]>([]);

    // 날짜 포맷 함수
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const year = String(date.getFullYear()).slice(2);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `'${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
    };

    useEffect(() => {
        fetchBoards();  // fetchBoards 내부에서 카테고리도 함께 가져옴
        fetchNoticeBoard();
    }, []);

    const fetchBoards = async () => {
        try {
            setLoading(true);
            const response = await staffClient.get('/board/general', { params: { limit: 1000 } });
            const boardData = response.data?.data?.data || response.data?.data || response.data || [];

            // 게시판 데이터는 이미 categories를 포함하고 있음
            const sortedData = boardData.sort((a: Board, b: Board) => a.order - b.order);
            setBoards(sortedData);
            setSortedBoards(sortedData);

            // 전체 카테고리 목록 추출 (통계용)
            const allCategories: Category[] = [];
            boardData.forEach((board: Board) => {
                if (board.categories) {
                    allCategories.push(...board.categories);
                }
            });
            setCategories(allCategories);
        } catch (error) {
            console.error('[BoardManagement] fetchBoards error:', error);
            // 서버 연결 실패 시 목업 데이터 사용
            const mockBoards = [
                { id: 1, name: '자유게시판', description: '자유롭게 소통하는 공간', visible: true, order: 1, created_at: '2025-08-01', updated_at: '2025-08-01', categories: [] },
                { id: 2, name: '정보공유', description: '업무 관련 정보를 공유하는 공간', visible: true, order: 2, created_at: '2025-08-01', updated_at: '2025-08-01', categories: [] },
                { id: 3, name: '사진자료실', description: '사진과 이미지를 공유하는 공간', visible: true, order: 3, created_at: '2025-08-01', updated_at: '2025-08-01', categories: [] },
                { id: 4, name: '비공개게시판', description: '비공개 테스트 게시판', visible: false, order: 4, created_at: '2025-08-01', updated_at: '2025-08-01', categories: [] }
            ];
            setBoards(mockBoards);
            setSortedBoards(mockBoards);
        } finally {
            setLoading(false);
        }
    };

    // fetchCategories 함수 제거 - fetchBoards에서 이미 카테고리 데이터를 받음

    const fetchNoticeBoard = async () => {
        try {
            const response = await staffClient.get('/board/notice');
            setNoticeBoard(response.data);
        } catch (error) {
            console.error('[BoardManagement] fetchNoticeBoard error:', error);
        }
    };

    const handleCreate = async () => {
        try {
            const response = await staffClient.post('/board', {
                ...formData,
                order: boards.length + 1
            });
            const newBoard = response.data;

            // 카테고리 생성
            for (let i = 0; i < boardCategories.length; i++) {
                await categoryApi.create({
                    name: boardCategories[i].name,
                    boardId: newBoard.id,
                    order: i + 1
                });
            }

            await fetchBoards();

            setShowCreateModal(false);
            resetForm();
            alert('게시판이 생성되었습니다.');
        } catch (error) {
            console.error('[BoardManagement] create error:', error);
            alert('게시판 생성에 실패했습니다.');
        }
    };

    const handleEdit = async () => {
        if (!editingBoard) return;

        try {
            // 게시판 정보 수정 (order 제외)
            await staffClient.patch(`/board/${editingBoard.id}`, {
                name: formData.name,
                description: formData.description,
                visible: formData.visible
            });

            // 기존 카테고리와 새 카테고리 비교
            const existingCategories = editingBoard.categories || [];
            const existingIds = existingCategories.map(cat => cat.id.toString());
            const currentIds = boardCategories.filter(cat => cat.id && !cat.id.startsWith('new-')).map(cat => cat.id);

            // 삭제할 카테고리
            const toDelete = existingIds.filter(id => !currentIds.includes(id));
            for (const id of toDelete) {
                await categoryApi.delete(parseInt(id));
            }

            // 새로 추가할 카테고리
            const toCreate = boardCategories.filter(cat => cat.id.startsWith('new-'));
            for (let i = 0; i < toCreate.length; i++) {
                await categoryApi.create({
                    name: toCreate[i].name,
                    boardId: editingBoard.id,
                    order: boardCategories.indexOf(toCreate[i]) + 1
                });
            }

            // 기존 카테고리 이름 및 순서 업데이트
            const toUpdate = boardCategories.filter(cat => !cat.id.startsWith('new-'));
            for (const cat of toUpdate) {
                const existingCat = existingCategories.find(c => c.id.toString() === cat.id);
                const newOrder = boardCategories.indexOf(cat) + 1;

                // 이름이나 순서가 변경된 경우에만 업데이트
                if (existingCat && (existingCat.name !== cat.name || existingCat.order !== newOrder)) {
                    await categoryApi.update(parseInt(cat.id), {
                        name: cat.name,
                        order: newOrder
                    });
                }
            }

            await fetchBoards();

            setShowEditModal(false);
            setEditingBoard(null);
            resetForm();
            alert('게시판이 수정되었습니다.');
        } catch (error) {
            console.error('[BoardManagement] edit error:', error);
            alert('게시판 수정에 실패했습니다.');
        }
    };

    const handleDelete = async (board: Board) => {
        // 공지사항 게시판은 삭제 불가
        if (board.board_type === 'notice') {
            alert('공지사항 게시판은 삭제할 수 없습니다.');
            return;
        }

        if (!window.confirm(`"${board.name}" 게시판을 삭제하시겠습니까?\n게시판의 모든 카테고리와 게시글도 함께 삭제됩니다.`)) return;

        try {
            await staffClient.delete(`/board/${board.id}`);

            await fetchBoards();
            alert('게시판이 삭제되었습니다.');
        } catch (error) {
            console.error('[BoardManagement] delete error:', error);
            alert('게시판 삭제에 실패했습니다.');
        }
    };

    const handleToggleVisible = async (board: Board) => {
        try {
            await staffClient.patch(`/board/${board.id}`, {
                visible: !board.visible
            });
            await fetchBoards();
        } catch (error) {
            console.error('[BoardManagement] toggle visible error:', error);
            alert('공개 설정 변경에 실패했습니다.');
        }
    };

    // 게시판 순서 저장 (batch API 사용)
    const handleSaveBoardOrder = async () => {
        try {
            // 게시판 순서 데이터 준비
            const boardsData = sortedBoards.map((board, index) => ({
                id: board.id,
                order: index + 1
            }));

            // batch API 호출
            const response = await staffClient.post('/board/batch-update-order', {
                boards: boardsData
            });

            if (response.data?.success) {
                await fetchBoards();
                setShowOrderModal(false);
                alert('게시판 순서가 저장되었습니다.');
            } else {
                throw new Error(response.data?.message || '순서 저장 실패');
            }
        } catch (error) {
            console.error('[BoardManagement] save board order error:', error);
            alert('게시판 순서 저장에 실패했습니다.');
        }
    };

    // 게시판 드래그 앤 드롭 처리
    const handleBoardDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(sortedBoards);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setSortedBoards(items);
    };

    // 카테고리 드래그 앤 드롭 처리
    const handleCategoryDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(boardCategories);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // 순서 재정렬
        const reorderedCategories = items.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        setBoardCategories(reorderedCategories);
    };

    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const openEditModal = (board: Board) => {
        setEditingBoard(board);
        setFormData({
            name: board.name,
            description: board.description,
            visible: board.visible
        });

        // 해당 게시판의 카테고리 로드 (board 객체에 이미 포함되어 있음)
        const boardCats = (board.categories || [])
            .sort((a, b) => a.order - b.order)
            .map(cat => ({
                id: cat.id.toString(),
                name: cat.name,
                order: cat.order
            }));
        setBoardCategories(boardCats);

        setShowEditModal(true);
    };

    const openOrderModal = () => {
        setSortedBoards([...boards]);
        setShowOrderModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            visible: true
        });
        setBoardCategories([]);
        setNewCategoryName('');
        setEditingCategoryId(null);
        setEditingCategoryName('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));
    };

    // 카테고리 관리 함수들
    const addCategory = () => {
        if (newCategoryName.trim()) {
            const newCategory: CategoryItem = {
                id: `new-${Date.now()}`,
                name: newCategoryName.trim(),
                order: boardCategories.length + 1
            };
            setBoardCategories([...boardCategories, newCategory]);
            setNewCategoryName('');
        }
    };

    const removeCategory = (categoryId: string) => {
        const filtered = boardCategories.filter(c => c.id !== categoryId);
        const reordered = filtered.map((cat, idx) => ({
            ...cat,
            order: idx + 1
        }));
        setBoardCategories(reordered);
    };

    const startEditCategory = (categoryId: string) => {
        const category = boardCategories.find(c => c.id === categoryId);
        if (category) {
            setEditingCategoryId(categoryId);
            setEditingCategoryName(category.name);
        }
    };

    const saveEditCategory = () => {
        if (editingCategoryId && editingCategoryName.trim()) {
            setBoardCategories(boardCategories.map(cat =>
                cat.id === editingCategoryId
                    ? { ...cat, name: editingCategoryName.trim() }
                    : cat
            ));
            setEditingCategoryId(null);
            setEditingCategoryName('');
        }
    };

    const cancelEditCategory = () => {
        setEditingCategoryId(null);
        setEditingCategoryName('');
    };

    // 공지사항과 일반 게시판을 합친 데이터
    const allBoards = noticeBoard ? [noticeBoard, ...boards] : boards;

    // Table columns 정의
    const columns: Column<Board>[] = [
        {
            key: 'order',
            header: '순서',
            width: '64px',
            align: 'center',
            render: (value, row) => {
                const isNotice = row.board_type === 'notice';
                return (
                    <span
                        className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium tabular-nums',
                            isNotice ? 'bg-navy-50 text-primary' : 'bg-neutral-100 text-neutral-700'
                        )}
                        title={isNotice ? '시스템 게시판' : undefined}
                    >
                        {isNotice ? <Megaphone className="h-3.5 w-3.5" /> : value}
                    </span>
                );
            }
        },
        {
            key: 'name',
            header: '게시판명',
            width: '180px',
            render: (value, row) => (
                <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{value}</span>
                    {row.board_type === 'notice' && <Badge variant="outline">시스템</Badge>}
                </div>
            )
        },
        {
            key: 'description',
            header: '설명',
            // width 제거 - 남은 공간 모두 차지하도록
            render: (value) => (
                <div className="truncate text-foreground" title={value}>{value}</div>
            )
        },
        {
            key: 'categories',
            header: '카테고리',
            width: '300px',
            render: (value: Category[] | undefined) => {
                const sortedCats = value ? [...value].sort((a, b) => a.order - b.order) : [];
                return (
                    <div className="flex flex-wrap gap-1">
                        {sortedCats.length > 0 ? (
                            sortedCats.map((cat) => (
                                <Badge key={cat.id} variant="secondary" className="whitespace-nowrap">
                                    {cat.name}
                                </Badge>
                            ))
                        ) : (
                            <span className="text-xs text-neutral-400">카테고리 없음</span>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'visible',
            header: '공개',
            width: '80px',
            align: 'center',
            render: (value, row) => {
                const isNotice = row.board_type === 'notice';
                if (isNotice) {
                    return <Badge variant="success">공개</Badge>;
                }
                return (
                    <button
                        type="button"
                        title="클릭하여 공개 여부 전환"
                        className="rounded-full transition-opacity duration-fast hover:opacity-80"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleVisible(row);
                        }}
                    >
                        <Badge variant={value ? 'success' : 'secondary'} className="whitespace-nowrap">
                            {value ? '공개' : '비공개'}
                        </Badge>
                    </button>
                );
            }
        },
        {
            key: 'created_at',
            header: '생성일',
            width: '140px',
            render: (value) => <span className="whitespace-nowrap tabular-nums">{formatDate(value)}</span>
        },
        {
            key: 'updated_at',
            header: '수정일',
            width: '140px',
            render: (value) => <span className="whitespace-nowrap tabular-nums">{formatDate(value)}</span>
        }
    ];

    // 생성/수정 모달 공용 폼 (droppableId만 다름)
    const renderBoardForm = (droppableId: string) => (
        <div className="space-y-4">
            <Input
                label="게시판명"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="게시판명을 입력하세요"
            />
            <div className="flex flex-col gap-1.5">
                <label htmlFor={`${droppableId}-description`} className="text-sm font-medium text-neutral-700">
                    설명
                </label>
                <textarea
                    id={`${droppableId}-description`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className={textareaClass}
                    placeholder="게시판 설명을 입력하세요"
                />
            </div>

            {/* 카테고리 관리 섹션 */}
            <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-neutral-700">카테고리 관리</span>
                <div className="rounded border border-border p-3">
                    <div className="mb-3 flex items-center gap-2">
                        <Input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                            placeholder="카테고리명 입력"
                        />
                        <Button type="button" variant="outline" className="shrink-0" onClick={addCategory}>
                            추가
                        </Button>
                    </div>

                    {boardCategories.length > 0 && (
                        <DragDropContext onDragEnd={handleCategoryDragEnd}>
                            <Droppable droppableId={droppableId}>
                                {(provided: DroppableProvided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1.5">
                                        {boardCategories.map((category, index) => (
                                            <Draggable key={category.id} draggableId={category.id} index={index}>
                                                {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={cn(
                                                            'flex items-center gap-2 rounded border px-2 py-1.5',
                                                            snapshot.isDragging
                                                                ? 'border-navy-100 bg-navy-50'
                                                                : 'border-border bg-neutral-50'
                                                        )}
                                                    >
                                                        <div {...provided.dragHandleProps} className="text-neutral-400">
                                                            <GripVertical className="h-4 w-4" />
                                                        </div>
                                                        <span className="w-5 shrink-0 text-center text-xs tabular-nums text-muted-foreground">
                                                            {index + 1}
                                                        </span>
                                                        {editingCategoryId === category.id ? (
                                                            <>
                                                                <Input
                                                                    type="text"
                                                                    value={editingCategoryName}
                                                                    onChange={(e) => setEditingCategoryName(e.target.value)}
                                                                    onKeyDown={(e) => e.key === 'Enter' && saveEditCategory()}
                                                                    className="h-8"
                                                                    autoFocus
                                                                />
                                                                <IconButton label="저장" onClick={saveEditCategory}>
                                                                    <Check />
                                                                </IconButton>
                                                                <IconButton label="취소" onClick={cancelEditCategory}>
                                                                    <X />
                                                                </IconButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span
                                                                    className="flex-1 cursor-pointer text-sm text-foreground"
                                                                    onClick={() => startEditCategory(category.id)}
                                                                >
                                                                    {category.name}
                                                                </span>
                                                                <IconButton label="삭제" danger onClick={() => removeCategory(category.id)}>
                                                                    <X />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}

                    {boardCategories.length === 0 && (
                        <p className="py-2 text-center text-xs text-neutral-400">카테고리를 추가해주세요</p>
                    )}
                </div>
            </div>

            <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-foreground">
                <input
                    type="checkbox"
                    name="visible"
                    checked={formData.visible}
                    onChange={handleInputChange}
                    className="h-4 w-4 rounded border-input accent-navy-700"
                />
                공개
            </label>
        </div>
    );

    if (loading) {
        return (
            <PageContainer>
                <Loading text="게시판 정보를 불러오는 중..." />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="게시판 관리"
                subtitle="게시판을 생성, 수정, 삭제하고 카테고리를 관리할 수 있습니다."
                actions={
                    <>
                        <Button variant="outline" icon={<ListOrdered />} onClick={openOrderModal}>
                            게시판 순서 관리
                        </Button>
                        <Button variant="primary" icon={<Plus />} onClick={openCreateModal}>
                            게시판 생성
                        </Button>
                    </>
                }
            />

            <StatsGrid columns={4} className="mb-4">
                <StatCard
                    title="전체 게시판"
                    value={boards.length + (noticeBoard ? 1 : 0)}
                    suffix="개"
                />

                <StatCard
                    title="공개 게시판"
                    value={boards.filter(b => b.visible).length}
                    suffix="개"
                />

                <StatCard
                    title="비공개 게시판"
                    value={boards.filter(b => !b.visible).length}
                    suffix="개"
                />

                <StatCard
                    title="전체 카테고리"
                    value={categories.length}
                    suffix="개"
                />
            </StatsGrid>

            {/* 게시판 목록 */}
            <TableContainer
                title="게시판 목록"
                resultCount={allBoards.length}
            >
                <Table
                    columns={columns}
                    data={allBoards}
                    stickyHeader
                    emptyMessage="등록된 게시판이 없습니다."
                    rowClassName={(row) => row.board_type === 'notice' ? 'bg-neutral-50' : ''}
                    onRowClick={(board) => openEditModal(board)}
                    actionsColumnWidth="80px"
                    actions={(board) =>
                        board.board_type === 'notice' ? (
                            <span className="whitespace-nowrap text-xs text-neutral-400">삭제 불가</span>
                        ) : (
                            <Button
                                size="small"
                                variant="danger-outline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(board);
                                }}
                            >
                                삭제
                            </Button>
                        )
                    }
                />
            </TableContainer>

            {/* 게시판 순서 관리 모달 */}
            <Modal
                isOpen={showOrderModal}
                onClose={() => setShowOrderModal(false)}
                title="게시판 순서 관리"
                size="medium"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowOrderModal(false)}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleSaveBoardOrder}>
                            저장
                        </Button>
                    </>
                }
            >
                <p className="mb-3 text-sm text-muted-foreground">드래그하여 게시판 순서를 변경하세요.</p>

                <DragDropContext onDragEnd={handleBoardDragEnd}>
                    <Droppable droppableId="boards">
                        {(provided: DroppableProvided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1.5">
                                {sortedBoards.map((board, index) => (
                                    <Draggable key={board.id} draggableId={board.id.toString()} index={index}>
                                        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={cn(
                                                    'flex items-center gap-3 rounded border px-3 py-2 transition-colors duration-fast',
                                                    snapshot.isDragging
                                                        ? 'border-navy-100 bg-navy-50'
                                                        : 'border-border bg-card hover:border-neutral-300'
                                                )}
                                            >
                                                <GripVertical className="h-4 w-4 text-neutral-400" />
                                                <span className="w-5 text-center text-sm tabular-nums text-muted-foreground">
                                                    {index + 1}
                                                </span>
                                                <span className="flex-1 text-sm font-medium text-foreground">{board.name}</span>
                                                <Badge variant={board.visible ? 'success' : 'secondary'}>
                                                    {board.visible ? '공개' : '비공개'}
                                                </Badge>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </Modal>

            {/* 생성 모달 */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="게시판 생성"
                size="large"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleCreate}>
                            생성
                        </Button>
                    </>
                }
            >
                {renderBoardForm('categories')}
            </Modal>

            {/* 수정 모달 */}
            <Modal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                title="게시판 수정"
                size="large"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowEditModal(false)}>
                            취소
                        </Button>
                        <Button variant="primary" onClick={handleEdit}>
                            수정
                        </Button>
                    </>
                }
            >
                {renderBoardForm('categories-edit')}
            </Modal>
        </PageContainer>
    );
};

const IconButton: React.FC<{
    label: string;
    onClick: () => void;
    danger?: boolean;
    children: React.ReactNode;
}> = ({ label, onClick, danger = false, children }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={label}
        title={label}
        className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-500 transition-colors duration-fast hover:bg-muted [&_svg]:h-4 [&_svg]:w-4',
            danger ? 'hover:text-destructive' : 'hover:text-foreground'
        )}
    >
        {children}
    </button>
);

export default BoardManagement;
